import { useEffect, useState } from 'react'
import { api, type ApiError } from '../../lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'
import { Badge, StatusBox } from '../ui/badge'
import { FloatingInput } from '../ui/input'

const TH = 'h-10 px-2 text-left text-xs font-medium text-muted-foreground'

export function HetznerSection({ accessToken }: { accessToken: string | null }) {
  const [status, setStatus] = useState<{
    connected: boolean
    accountLabel: string | null
    serverCount: number
    monthlyCostEur: number
  } | null>(null)
  const [servers, setServers] = useState<
    Array<{
      id: string
      hetznerName: string
      serverType: string
      role: 'staging' | 'prod'
      boundDomain: string | null
      monthlyCostEur: number
      status: string
    }>
  >([])
  const [tokenInput, setTokenInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)

  useEffect(() => {
    if (!accessToken) return
    api.hetzner
      .status(accessToken)
      .then((s) => setStatus(s))
      .catch(() => setStatus({ connected: false, accountLabel: null, serverCount: 0, monthlyCostEur: 0 }))
  }, [accessToken])

  useEffect(() => {
    if (!accessToken || !status?.connected) return
    api.hetzner
      .listServers(accessToken)
      .then((r) => setServers(r.servers))
      .catch(() => setServers([]))
  }, [accessToken, status?.connected])

  async function onConnect(): Promise<void> {
    if (!accessToken || !tokenInput.trim()) return
    setSaving(true)
    setErr(null)
    setOk(null)
    try {
      const s = await api.hetzner.connect(accessToken, tokenInput.trim())
      setStatus(s)
      setTokenInput('')
      setShowAdvanced(false)
      setOk('Hetzner connected. Provisioning will use this account.')
    } catch (e) {
      setErr(((e as ApiError).detail ?? (e as ApiError).title) ?? 'Failed to connect')
    } finally {
      setSaving(false)
    }
  }

  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  async function onDisconnect(): Promise<void> {
    if (!accessToken) return
    setErr(null)
    setOk(null)
    try {
      await api.hetzner.disconnect(accessToken)
      setStatus({ connected: false, accountLabel: null, serverCount: 0, monthlyCostEur: 0 })
      setServers([])
      setOk('Hetzner disconnected. Existing servers stay live; new provisioning is paused.')
    } catch (e) {
      setErr(((e as ApiError).detail ?? (e as ApiError).title) ?? 'Failed to disconnect')
    }
  }

  if (!accessToken) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Hetzner Cloud
          {status === null ? null : status.connected ? (
            <Badge variant="success">Connected</Badge>
          ) : (
            <Badge variant="outline">Not connected</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Doable provisions servers on your own Hetzner account. The bill comes
          from Hetzner directly to you — Doable never charges for infra. Connect
          a token with Read &amp; Write scope.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {status === null ? (
          <div className="dwlt-skeleton h-12" />
        ) : status.connected ? (
          <>
            <StatusBox tone="success" className="flex items-center justify-between gap-3">
              <div>
                Connected · {status.accountLabel ?? 'unknown account'}
                <span className="block text-xs opacity-80 tabular-nums">
                  {status.serverCount} {status.serverCount === 1 ? 'server' : 'servers'} ·
                  €{status.monthlyCostEur.toFixed(2)}/month
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={() => setConfirmDisconnect(true)} className="bg-card">
                Disconnect
              </Button>
            </StatusBox>

            {servers.length > 0 && (
              <div className="overflow-x-auto rounded-xl border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className={TH}>Name</th>
                      <th className={TH}>Role</th>
                      <th className={TH}>Type</th>
                      <th className={TH}>Domain / Use</th>
                      <th className={`${TH} text-right`}>€/mo</th>
                      <th className={TH}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servers.map((s) => (
                      <tr key={s.id} className="border-b last:border-b-0 hover:bg-muted/50">
                        <td className="p-2 font-mono text-xs">{s.hetznerName}</td>
                        <td className="p-2">
                          <Badge variant={s.role === 'staging' ? 'volt' : 'success'}>{s.role}</Badge>
                        </td>
                        <td className="p-2 text-muted-foreground">{s.serverType}</td>
                        <td className="p-2 text-muted-foreground">
                          {s.boundDomain ?? <span className="text-muted-foreground/70">all projects</span>}
                        </td>
                        <td className="p-2 text-right tabular-nums">€{s.monthlyCostEur.toFixed(2)}</td>
                        <td className="p-2 text-xs text-muted-foreground">{s.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {ok && <StatusBox tone="success">{ok}</StatusBox>}
            {err && <StatusBox tone="destructive">{err}</StatusBox>}
          </>
        ) : (
          <>
            <StatusBox tone="volt">
              Generate a token at{' '}
              <a
                href="https://console.hetzner.cloud"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline underline-offset-2"
              >
                console.hetzner.cloud
              </a>{' '}
              → Project → Security → API tokens → Generate. Pick{' '}
              <strong>Read &amp; Write</strong> scope.
            </StatusBox>

            <details
              className="rounded-lg border"
              open={showAdvanced}
              onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)}
            >
              <summary className="select-none px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {showAdvanced ? '−' : '+'} Paste your Hetzner API token
              </summary>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  void onConnect()
                }}
                className="space-y-3 p-3 pt-1"
              >
                <FloatingInput
                  label="Hetzner API token"
                  type="password"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  placeholder="64-character Hetzner API token"
                  className="font-mono"
                  autoComplete="off"
                  minLength={64}
                  maxLength={64}
                />
                {err && <StatusBox tone="destructive">{err}</StatusBox>}
                <Button type="submit" disabled={saving || tokenInput.trim().length !== 64}>
                  {saving ? 'Validating…' : 'Connect Hetzner account'}
                </Button>
              </form>
            </details>

            {ok && <StatusBox tone="success">{ok}</StatusBox>}
          </>
        )}
      </CardContent>
      <Dialog
        open={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        title="Disconnect Hetzner?"
        description="The stored token is removed. Existing servers stay live; new provisioning and server management stop until you reconnect."
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={() => { setConfirmDisconnect(false); void onDisconnect() }}>Disconnect</Button>
          </>
        }
      />
    </Card>
  )
}
